import { Divider, Stack } from "@mui/material";
import { Button, Checkbox, Dialog, HSpacer, Text, VSpacer } from "@/components/DesignSystem";
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { PromotionInputs } from "@/pages/Promotions/PromotionModal/PromotionModal";
import { useSnackbar } from "@/providers/GlobalSnackbarProvider";
import { ApiRetailer, ApiRetailerLocation } from "@api/interfaces";

interface EditLocationsModalProps {
  isAllLocationsLocked?: boolean;
  locationIdsWithPointsAwarded?: string[];
  onClose: () => void;
  onPromotionChanged: (promotion: PromotionInputs) => void;
  promotion: PromotionInputs;
  retailerLocations?: ApiRetailerLocation[];
  selectedRetailer: ApiRetailer,
}

export const EditLocationsModal = ({
  isAllLocationsLocked,
  locationIdsWithPointsAwarded,
  onClose,
  onPromotionChanged,
  promotion,
  retailerLocations,
  selectedRetailer,
} : EditLocationsModalProps) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();

  const [allLocations, setAllLocations] = useState(promotion.allLocations === true);
  const [locationIds, setLocationIds] = useState<string[] | undefined>(promotion.locationIds);

  const total = retailerLocations?.length ?? 0;
  const selectedCount = locationIds?.length ?? 0;
  const allChecked = allLocations;
  const someChecked = !allChecked && selectedCount > 0;

  const handleAllLocationsChange = () => {
    if (allChecked) {
      setAllLocations(false);
      setLocationIds([]);
    } else {
      setAllLocations(true);
      setLocationIds(undefined);
    }
  };

  return <Dialog
    acceptButton={(props) => (
      <Button
        {...props}
        disabled={!allLocations && !locationIds?.length}
        onClick={() => {
          onPromotionChanged({
            ...promotion,
            allLocations: allLocations,
            locationIds: locationIds,
          });
          onClose();
        }}
        testID="location-modal-done-button"
      >
        {t($ => $.common.actions.done)}
      </Button>
    )}
    cancelButton={(props) => (
      <Button
        {...props}
        onClick={onClose}
        testID="location-modal-cancel-button"
      >
        {t($ => $.common.actions.cancel)}
      </Button>
    )}
    onClose={onClose}
    open
    showCloseButton={false}
    testID='edit-locations-modal'
    title={t($ => $.promotions.create.editLocations.title, { retailerName: selectedRetailer.name })}
  >
    <Stack alignItems="center" direction="row" justifyContent="space-between">
      <Checkbox
        checked={allChecked}
        disabled={isAllLocationsLocked}
        indeterminate={someChecked}
        onChangeChecked={handleAllLocationsChange}
        testID="location-modal-all-locations-checkbox"
      >
        <Text category="title-medium">
          {t($ => $.promotions.create.editLocations.allLocations)}
        </Text>
      </Checkbox>
      <Text category="body-medium" sx={{ color: 'text.secondary' }}>
        {allChecked
          ? t($ => $.promotions.create.editLocations.includesFutureLocations)
          : t($ => $.promotions.create.editLocations.selectedCount, {
            selected: selectedCount,
            total,
          })
        }
      </Text>
    </Stack>
    <VSpacer size="5" />
    <Divider />
    {allChecked && (
      <>
        <VSpacer size="5" />
        <Text category="body-medium" sx={{ color: 'text.secondary' }}>
          {t($ => $.promotions.create.editLocations.allLocationsDescription)}
        </Text>
      </>
    )}
    {!!retailerLocations && retailerLocations.map(location => {
      const isChecked = allChecked || (locationIds?.includes(location.id) ?? false);
      const toggleLocation = () => {
        if (allChecked) return;
        const updatedSet = new Set(locationIds ?? []);
        if (isChecked) {
          if (locationIdsWithPointsAwarded?.includes(location.id)) {
            openSnackbar(t($ => $.promotions.create.editLocations.pointsAlreadyAwarded));
            return;
          }
          updatedSet.delete(location.id);
        } else {
          updatedSet.add(location.id);
        }
        setLocationIds([...updatedSet]);
      };
      return (
        <Stack key={location.id}>
          <Stack
            onClick={allChecked ? undefined : toggleLocation}
            sx={{ cursor: allChecked ? 'default' : 'pointer' }}
          >
            <VSpacer size="5" />
            <Stack alignItems="center" direction="row">
              <HSpacer size="5" />
              <Checkbox
                checked={isChecked}
                disabled={allChecked}
                onChangeChecked={toggleLocation}
                onClick={(e) => e.stopPropagation()}
                testID={`set-selected-location-checkbox-id-${location.id}`}
              >
                <Text sx={allChecked ? { color: 'text.disabled' } : {}}>
                  {location.name}
                </Text>
              </Checkbox>
            </Stack>
            <VSpacer size="5" />
          </Stack>
          <Divider />
        </Stack>
      );
    })}
    <VSpacer size="7" />
  </Dialog>;
};
